/**
 * Dashboard_N figures (#2139, epic #2138): every number the Month view card
 * shows, derived from one `/flows` month entry and `/tangible`. Every input
 * is the SIGNED sum `/flows` keeps (spending negative, income positive), so
 * the code adds where the page's words subtract. The words for each figure
 * are in docs/dashboard-n-figures.md and the equations in
 * docs/dashboard-equations.md; the names here follow the epic's vocabulary
 * table.
 */

const cents = (m, field) => (m && m[field]) || 0;

/** Spending lines tagged to a budget: all spending less the untagged part. */
export const spendingFromBudgetsCents = (m) => cents(m, 'spendingCents') - cents(m, 'unbudgetedSpendingCents');

/** Net outlier money: one-time out (negative) plus one-time in. */
export const outliersCents = (m) => cents(m, 'oneTimeInCents') + cents(m, 'oneTimeOutCents');

/** Baseline net: income less all spending; transfers and outliers set aside. */
export const baselineNetCents = (m) => cents(m, 'incomeCents') + cents(m, 'spendingCents');

/** Net with transfers counted: Baseline net plus the month's net transfers. */
export const withTransfersCents = (m) => baselineNetCents(m) + cents(m, 'transferNetCents');

/**
 * Net with transfers and outliers counted ("Net at close" on a
 * closed month): the chart's Raw bar.
 */
export const everythingCents = (m) => withTransfersCents(m) + outliersCents(m);

/**
 * Net, all budgets spent, current month only: income, less spending
 * outside budgets, less the budgeted funds still remaining (`/tangible`
 * `positiveBudgetCents`, positive Normal balances only).
 */
export const netIfSpentCents = (m, positiveBudgetCents) =>
  cents(m, 'incomeCents') + cents(m, 'unbudgetedSpendingCents') - (positiveBudgetCents || 0);

/**
 * Checking, the top bar's first figure (#2206): the active Checking ledgers'
 * balances from `/tangible` `byAccount` (Checking and Credit Card rows carry
 * `type` and `archived`). Archived ledgers stay out (owner, #2139 settle
 * point 3).
 * @param {Array<{type: number, archived?: boolean, balanceCents: number}>} byAccount
 */
export const checkingCents = (byAccount) => (byAccount || [])
  .filter((l) => l.type === 0 && !l.archived)
  .reduce((sum, l) => sum + (l.balanceCents || 0), 0);

/**
 * The month switcher's list (#2139): the current month always, plus every
 * month with lines, newest first. Each carries `closed` so the dropdown can
 * say "in progress" or "closed".
 * @param {Array<{yearMonth: string, lineCount: number}>} months  oldest first
 * @param {string} currentYearMonth
 */
export const switcherMonths = (months, currentYearMonth) => (months || [])
  .filter((m) => m.yearMonth === currentYearMonth || (m.lineCount || 0) > 0)
  .map((m) => ({ yearMonth: m.yearMonth, closed: m.yearMonth !== currentYearMonth }))
  .reverse();
